import React, { createContext, useContext, useState, useEffect } from 'react';
import { getToken, getUser, setToken, setUser, clearToken, isTokenExpired, setActiveCustomerId } from '../utils/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUserState] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Staff "View as client" hand-off: portal_url has ?impersonate_token=<jwt>.
    // Pull it out, store as the active session token, strip from URL so a
    // refresh doesn't re-process it. Decode minimal user info from the JWT.
    const params = new URLSearchParams(window.location.search);
    const impToken = params.get('impersonate_token');
    if (impToken) {
      try {
        const payload = JSON.parse(atob(impToken.split('.')[1]));
        if (payload?.type === 'portal') {
          setToken(impToken);
          // Drop any customer selection left in localStorage by a PREVIOUS
          // session. An impersonation is explicitly scoped to the customer the
          // staffer picked, so inheriting a stale macula_active_customer_id
          // meant the app sent X-Macula-Customer-Id for a different client
          // entirely — the backend silently falls back to the token's customer
          // when that id isn't accessible, which made the customer switcher
          // appear/disappear unpredictably between impersonations.
          setActiveCustomerId(null);
          const userInfo = {
            id: payload.sub,
            email: payload.email,
            customer_id: payload.customer_id,
            impersonated_by: payload.impersonated_by,
            impersonated_by_name: payload.impersonated_by_name,
          };
          setUser(userInfo);
          setUserState(userInfo);
          setIsAuthenticated(true);
          // Strip the token from the URL so it doesn't sit in history /
          // browser title and isn't accidentally shared via screenshot.
          params.delete('impersonate_token');
          const cleanQuery = params.toString();
          window.history.replaceState(
            {}, '', window.location.pathname + (cleanQuery ? '?' + cleanQuery : '') + window.location.hash,
          );
          setIsLoading(false);
          return;
        }
      } catch (e) {
        console.warn('Bad impersonate_token, ignoring:', e);
      }
    }

    // Check for existing session
    const token = getToken();
    const savedUser = getUser();

    if (token && savedUser && !isTokenExpired(token)) {
      setUserState(savedUser);
      setIsAuthenticated(true);
    } else {
      clearToken();
    }
    setIsLoading(false);
  }, []);

  const login = (token, userInfo) => {
    setToken(token);
    setUser(userInfo);
    setUserState(userInfo);
    setIsAuthenticated(true);
  };

  const logout = () => {
    clearToken();
    setUserState(null);
    setIsAuthenticated(false);
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    setUserState(updatedUser);
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, isLoading, login, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
